import React, {useState, useEffect} from 'react';
import {useParams,} from 'react-router-dom';
import {
    DndContext,
    DragOverlay,
    closestCorners,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {Button, Card, Input, message, Modal, Dropdown, Segmented, Skeleton, Tooltip, ColorPicker} from 'antd';
import {
    InfoCircleOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';


import {SortableList} from './SortableList';
import TaskForm from './TaskForm';
import StatusManagement from './StatusManagement';
import LabelManagement from './LabelManagement';

import {useBoardDetail, useBoardLists, useBoardTasks, useBoardDragAndDrop} from './useBoardDetail';
import ListView from "@/components/taskManagement/ListView";
import {useRequest} from "ahooks";
import HttpClient from "@/utils/HttpClient";
import {useStatusManagement} from "@/components/taskManagement/useStatusManagement";


const BoardDetail = ({user, upgradePopup}) => {
    const {id} = useParams();
    const {data: users} = useRequest(async () => (await HttpClient.get(`/settings/user`))?.data?.users);

    const [currentView, setCurrentView] = useState('list');

    // Modal states
    const [listModal, setListModal] = useState({visible: false, type: 'add', data: null});
    const [taskModal, setTaskModal] = useState({
        visible: false,
        type: 'add',
        data: null,
        listId: null
    });
    const [labelModal, setLabelModal] = useState({visible: false});


    // Form states
    const [newListName, setNewListName] = useState('');
    const [newListColor, setNewListColor] = useState('#E2E8F0');

    const [taskForm, setTaskForm] = useState({
        title: '',
        description: '',
        status_id: null,
        due_date: null,
        priority: null,
        assigned_to: null,
        label_ids: [],
        documents: null,
        deleted_attachments: [],
    });

    // Initialize board state and operations
    const {
        board,
        lists,
        statuses,
        labels,
        loading,
        fetchBoardDetails,
        setLists,
        setStatuses,
        setLabels
    } = useBoardDetail(id);

    const {
        isStatusModalOpen,
        openStatusModal,
        closeStatusModal,
        loading: statusLoading,
        addStatus,
        updateStatus,
        deleteStatus
    } = useStatusManagement({
        boardId: id,
        statuses,
        onStatusesChange: setStatuses
    });
    // Initialize list operations
    const {addList, updateList, deleteList, reorderLists} = useBoardLists(id, lists, setLists);

    // Initialize task operations
    const {addTask, updateTask, deleteTask, moveTask} = useBoardTasks(id, lists, setLists);

    // Initialize drag and drop
    const {activeId, activeItem, handleDragStart, handleDragMove, handleDragEnd} = useBoardDragAndDrop(
        lists,
        setLists,
        moveTask,
        reorderLists
    );

    // Initialize sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        const loadBoard = async () => {
            const result = await fetchBoardDetails();
            if (!result.success) {
                window.location.href = ('/tm/ui-task-board');
            }
        };
        loadBoard();
    }, [id, fetchBoardDetails]);

    useEffect(() => {
        if (user?.price_plan && !user?.price_plan?.has_task_management) {
            upgradePopup();
            return true;
        }
    }, [user]);

    const handleListModalOk = async () => {
        if (listModal.type === 'add') {
            const result = await addList({
                name: newListName,
                color: newListColor
            });
            if (result.success) {
                setListModal({visible: false, type: 'add', data: null});
                setNewListName('');
                setNewListColor('#E2E8F0');
            }
        } else {
            try {
                const result = await updateList(listModal.data.id, {
                    name: newListName,
                    color: newListColor
                });
                if (result.success) {
                    setListModal({visible: false, type: 'edit', data: null});
                    setNewListName('');
                    setNewListColor('#E2E8F0');
                }
            } catch (err0a) {
                console.log("=>(BoardDetail.js:120) err0a", err0a);
            }

        }
        await fetchBoardDetails();
    };

    const handleTaskModalOk = async () => {
        // Validation checks
        if (!taskForm.title) {
            message.error('Please enter title');
            return false;
        }
        if (!taskForm.status_id) {
            message.error('Please enter status');
            return false;
        }
        if (typeof taskForm.priority === 'undefined' || taskForm.priority === null) {
            message.error('Please select priority');
            return false;
        }

        // Create FormData object
        const formData = new FormData();

        // Common form data
        const commonFields = {
            title: taskForm.title,
            description: taskForm.description || '',
            status_id: taskForm.status_id,
            priority: taskForm.priority,
            budget: taskForm.budget || '',
            assigned_to: taskForm.assigned_to,
            due_date: taskForm.due_date,
        };

        // Append common fields
        Object.entries(commonFields).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                formData.append(key, value);
            }
        });

        // Handle arrays
        if (taskForm.label_ids?.length) {
            taskForm.label_ids.forEach(labelId => {
                formData.append('label_ids[]', labelId);
            });
        }

        if (taskForm.documents?.length) {
            taskForm.documents.forEach(file => {
                formData.append('documents[]', file);
            });
        }

        try {
            let result;

            if (taskModal.type === 'add') {
                formData.append('list_id', taskForm.listId || taskModal.listId);
                formData.append('board_id', id);
                result = await addTask(formData);
            } else {
                // Handle deleted attachments properly by appending each ID separately
                if (taskForm.deleted_attachments?.length) {
                    taskForm.deleted_attachments.forEach(id => {
                        formData.append('deleted_attachments[]', id);
                    });
                }

                // Log the FormData content for debugging
                for (let pair of formData.entries()) {
                    console.log(pair[0] + ': ' + pair[1]);
                }

                result = await updateTask(taskModal.data.id, formData);
            }

            if (result.success) {
                setTaskModal({
                    visible: false,
                    type: taskModal.type === 'add' ? 'add' : 'edit',
                    data: null,
                    listId: null
                });
                resetTaskForm();
                await fetchBoardDetails();
            } else {
                message.error(result.message || 'Failed to save task');
            }
        } catch (error) {
            console.error('Error saving task:', error);
            message.error(error.response?.data?.message || 'An error occurred while saving the task');
        }
    };

    const resetTaskForm = () => {
        setTaskForm({
            title: '',
            description: '',
            status_id: null,
            due_date: null,
            priority: null,
            assigned_to: null,
            label_ids: [],
            documents: null,
            deleted_attachments: []
        });
    };


    const onAddMenuClick = (ev) => {
        if (ev?.key === 'list') {
            setListModal({visible: true, type: 'add'})
        } else if (ev?.key === 'task') {
            setTaskModal({visible: true, type: 'add', data: null, listId: lists?.[0].id});
        }
    };

    if (user?.price_plan && !user?.price_plan?.has_task_management) {
        return <div className={"h-full flex items-center justify-center"}>You need to upgrade your plan to use this
            feature</div>
    }

    const handleTaskSubmit = async (formData, type) => {
        try {
            let result;
            if (type === 'add') {
                result = await addTask(formData);
            } else {
                result = await updateTask(taskModal.data.id, formData);
            }

            if (result.success) {
                setTaskModal({
                    visible: false,
                    type: 'add',
                    data: null,
                    listId: null
                });
                await fetchBoardDetails();
            } else {
                message.error(result.message || 'Failed to save task');
            }
        } catch (error) {
            throw error;
        }
    };

    return (
        <div className="max-w-[90rem] h-full flex flex-col mx-auto pt-4 animate-fade-in">
            <div className="relative p-0 grow flex flex-col">
                <div className="pb-8">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <Button
                                type="link"
                                className="flex items-center text-gray-600 hover:text-blue-600 transition-colors"
                                onClick={() => window.location.href = '/tm/ui-task-board'}
                                icon={<ArrowLeftOutlined/>}
                            >
                                Boards
                            </Button>
                            <h2 className="text-xl text-[#096db7] m-0 flex items-center">
                                {board.name}
                                {board.description && (
                                    <Tooltip title={board.description} className={"font-normal"}>
                                        <sup>
                                            <InfoCircleOutlined
                                                className={"ml-1 text-xs text-gray-500 hover:text-blue-500"}/>
                                        </sup>
                                    </Tooltip>
                                )}
                            </h2>
                        </div>


                        <div className={'mr-[1px]'}>
                            <Dropdown.Button
                                type="primary"
                                className={"rounded-md shadow-2xl overflow-hidden"}
                                onClick={() => {
                                    setTaskModal({
                                        visible: true,
                                        type: 'add',
                                        data: null,
                                        listId: lists?.[0].id
                                    });
                                }}
                                menu={{
                                    items: [
                                        {
                                            key: 'task',
                                            label: 'Add a New Task',
                                        },
                                        {
                                            key: 'list',
                                            label: 'Add a New List',
                                        },
                                    ], onClick: onAddMenuClick
                                }}
                            >
                                New Task
                            </Dropdown.Button>
                        </div>
                    </div>
                </div>

                {/* Enhanced Lists Container */}
                <div className={'grow flex flex-col'}>
                    {currentView === 'kanban' ? (<DndContext
                        sensors={sensors}
                        collisionDetection={closestCorners}
                        onDragStart={handleDragStart}
                        onDragMove={handleDragMove}
                        onDragEnd={handleDragEnd}
                    >
                        <div className="flex overflow-x-auto pb-6 pt-2 px-2 -mx-2">
                            <SortableContext
                                items={lists.map(list => `list-${list.id}`)}
                                strategy={horizontalListSortingStrategy}
                            >
                                {lists.map((list) => (
                                    <div key={list.id} className="flex-shrink-0 mx-2">
                                        <SortableContext
                                            items={list.tasks.map(task => `task-${task.id}`)}
                                            id={`list-${list.id}`}
                                        >
                                            <SortableList
                                                list={list}
                                                onAddTask={() => setTaskModal({
                                                    visible: true,
                                                    type: 'add',
                                                    listId: list.id
                                                })}
                                                onEditList={(data) => {
                                                    setListModal({
                                                        visible: true,
                                                        type: 'edit',
                                                        data
                                                    });
                                                    setNewListName(data.name);
                                                    setNewListColor(data.color);
                                                }}
                                                onDeleteList={() => deleteList(list.id)}
                                            />
                                        </SortableContext>
                                    </div>
                                ))}
                            </SortableContext>
                        </div>

                        <DragOverlay>
                            {activeItem && activeId?.toString().startsWith('task-') ? (
                                <Card className="w-64 shadow-xl rounded-xl border-0 bg-white/90 backdrop-blur-sm">
                                    <div className="font-medium">{activeItem.title}</div>
                                </Card>
                            ) : activeItem && activeId?.toString().startsWith('list-') ? (
                                <div
                                    className="w-72 bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-xl border border-white/20">
                                    <h3 className="text-lg font-medium text-gray-800">{activeItem.name}</h3>
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>) : <ListView
                        lists={lists}
                        statuses={statuses}
                        openStatusModal={openStatusModal}
                        onUpdateTask={async (...data) => {
                            await updateTask(...data)
                            await fetchBoardDetails()
                        }}
                        onDeleteTask={(taskId, listId) => deleteTask(taskId, listId)}
                        onToggleVisibility={(taskId) => {
                            const task = lists.flatMap(l => l.tasks).find(t => t.id === taskId);
                            if (task) {
                                updateTask(taskId, {visible: !task.visible});
                            }
                        }}
                        onEditTask={(task) => {
                            setTaskForm({
                                title: task.title,
                                description: task.description,
                                status_id: task.status_id,
                                due_date: task.due_date,
                                priority: task.priority,
                                assigned_to: task.assigned_to,
                                budget: task.budget,
                                label_ids: task.labels?.map(l => l.id) || [],
                                deleted_attachments: [],
                                listId: task.list_id
                            });
                            setTaskModal({
                                visible: true,
                                type: 'edit',
                                data: task,
                                listId: lists.find(l =>
                                    l.tasks.some(t => t.id === task.id)
                                )?.id
                            });
                        }}
                        onAddTask={(listId) => {
                            setTaskModal({
                                visible: true,
                                type: 'add',
                                data: null,
                                listId
                            });
                        }}
                        onMoveTask={async (...args) => {
                            await moveTask(...args);
                            await fetchBoardDetails()
                        }}
                        onEditList={(list) => {
                            setListModal({
                                visible: true,
                                type: 'edit',
                                data: list
                            });
                            setNewListName(list.name);
                        }}
                        onDeleteList={deleteList}
                        activeId={activeId}
                        activeItem={activeItem}
                        handleDragStart={handleDragStart}
                        handleDragMove={handleDragMove}
                        handleDragEnd={handleDragEnd}
                        users={[user, ...(users || [])]}
                        currentUser={user}
                    />
                    }
                </div>

                {/* Enhanced Modals */}
                <Modal
                    title={listModal.type === 'add' ? 'Add New List' : 'Edit List'}
                    open={listModal.visible}
                    onOk={handleListModalOk}
                    okButtonProps={{
                        size: 'large',
                        className: "text-sm font-semibold leading-tight px-5 py-2.5 relative items-center rounded-full justify-center border border-gray-200 bg-[#3d8bfd]"
                    }}
                    cancelButtonProps={{
                        size: 'large',
                        className: "text-sm font-semibold leading-tight px-5 py-2.5 mr-6 relative items-center rounded-full justify-center border border-gray-200"
                    }}
                    onCancel={() => {
                        setListModal({visible: false, type: 'add', data: null});
                        setNewListName('');
                        setNewListColor('#E2E8F0');
                    }}
                    centered
                >
                    <div className="space-y-4 pt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                List Name
                            </label>
                            <Input
                                placeholder="Enter list name"
                                value={newListName}
                                onChange={(e) => setNewListName(e.target.value)}
                                size="large"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                List Color
                            </label>
                            <ColorPicker
                                value={newListColor}
                                onChange={(color) => setNewListColor(color.toHexString())}
                                size="large"
                                presets={[
                                    {
                                        label: 'Recommended',
                                        colors: [
                                            '#3B82F6',
                                            '#10B981',
                                            '#F59E0B',
                                            '#EF4444',
                                            '#8B5CF6',
                                            '#EC4899',
                                            '#6366F1',
                                            '#64748B'
                                        ],
                                    }
                                ]}
                            />
                        </div>
                    </div>
                </Modal>


                <Modal
                    title={taskModal.type === 'add' ? 'Create New Task' : 'Edit Task'}
                    open={taskModal.visible}
                    footer={null} // Remove default footer since TaskForm has its own buttons
                    onCancel={() => {
                        setTaskModal({
                            visible: false,
                            type: 'add',
                            data: null,
                            listId: null
                        });
                    }}
                    width={600}
                    centered
                    destroyOnClose={true}
                >
                    <TaskForm
                        initialData={taskModal.data}
                        type={taskModal.type}
                        onSubmit={handleTaskSubmit}
                        onCancel={() => {
                            setTaskModal({
                                visible: false,
                                type: 'add',
                                data: null,
                                listId: null
                            });
                        }}
                        statuses={statuses}
                        labels={labels}
                        lists={lists}
                        users={[user, ...(users || [])]}
                        boardId={id}
                    />
                </Modal>

                <StatusManagement
                    open={isStatusModalOpen}
                    onClose={closeStatusModal}
                    statuses={statuses}
                    boardId={id}
                    onStatusesChange={setStatuses}
                    loading={statusLoading}
                    onAdd={addStatus}
                    onUpdate={updateStatus}
                    onDelete={deleteStatus}
                />

                <LabelManagement
                    open={labelModal.visible}
                    onClose={() => setLabelModal({visible: false})}
                    labels={labels}
                    boardId={id}
                    onLabelsChange={setLabels}
                />
            </div>
        </div>
    );
};

export default BoardDetail;
